/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import { Task } from 'ember-concurrency';
import DS from 'ember-data';
import { ToggleDelta } from '@customerio/fly-components/addon/components/fly-grouped-checklist';
import AlertsService from 'cio/services/alerts';
import VerifyService from 'cio/services/verify';
import AnalyticsService from 'cio/services/analytics';
import { DATA_OUT_EVENTS } from 'cio/utils/base-events';
import IntegrationsDataOutTypeModel from 'cio/models/integrations/data-out-type';
import IntegrationModel from 'cio/models/integration';
import FormValidations from 'cio/utils/form-validations';
import RouterService from '@ember/routing/router-service';
import { tracked } from '@glimmer/tracking';
import AuthService from 'cio/services/auth';

interface DataOutIntegrationFormArgs {
  settings: IntegrationModel['settings'];
  dataModel: IntegrationsDataOutTypeModel;
  integration: IntegrationModel;
  readOnly: boolean;
  saveIntegrationTask: Task<unknown, []>;
  removeIntegrationTask: Task<unknown, []>;
}

export default class DataOutIntegrationForm extends Component<DataOutIntegrationFormArgs> {
  @service auth!: AuthService;
  @service alerts!: AlertsService;
  @service verify!: VerifyService;
  @service store!: DS.Store;
  @service analytics!: AnalyticsService;
  @service router!: RouterService;
  @tracked confirmAndRemoveModal = false;

  readonly validations: FormValidations;
  readonly groups = DATA_OUT_EVENTS;

  constructor(owner: unknown, args: DataOutIntegrationFormArgs) {
    super(owner, args);

    let Validations: { [key: string]: (value: string | undefined) => string | true } = {};
    args.settings?.forEach((setting) => {
      if (setting.default === null) {
        // null means required
        Validations[setting.key] = (value?: string) => {
          // existing integration field values for "secret: true" are not sent for security reasons, so we need to omit the validation on these fields
          if (args.dataModel.id && setting.secret) {
            return true;
          }

          if ((value?.trim().length ?? 0) === 0) {
            return `${setting.label} can't be blank`;
          }
          return true;
        };
      }
    });
    this.validations = FormValidations.create({
      validations: Validations,
      context: this,
      modelKey: 'args.dataModel.settings',
    });
  }

  willDestroy() {
    super.willDestroy();

    this.verify.verifyDataOutIntegration.cancelAll({ resetState: true });
  }

  get isFormDisabled() {
    let isReadonly = this.args.readOnly ?? false;
    return this.args.saveIntegrationTask.isRunning || isReadonly;
  }

  get isSaveDisabled() {
    // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
    return this.isFormDisabled || this.validations.hasErrors || !this.args.dataModel.hasDirtyAttributes;
  }

  get isSendTestDisabled() {
    let anySecretIsMissing =
      this.args.settings?.some(
        // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
        (setting) => setting.secret && !this.args.dataModel.settings[setting.key],
      ) ?? false;

    // eslint-disable-next-line
    return (
      anySecretIsMissing ||
      this.isFormDisabled ||
      this.validations.hasErrors ||
      this.verify.verifyDataOutIntegration.isRunning
    );
  }

  get events() {
    return this.args.dataModel.events ?? [];
  }

  get cannotEnableIntegration() {
    return !this.args.dataModel.enabled && !this.args.dataModel.hasEventsSelected;
  }

  get hasReadOnlyPermission() {
    if (!this.args.dataModel.get('isNew')) {
      return !this.auth.hasPermission('journeys/exports/edit');
    }

    return !this.auth.hasPermission('journeys/exports/create');
  }

  @action
  toggleEnabled() {
    this.args.dataModel.set('enabled', !this.args.dataModel.enabled);
  }

  @action
  async sendTest() {
    try {
      // this will throw if any field is invalid
      await this.validations.validateModel();
      if (this.isSendTestDisabled) {
        return;
      }
      let { settings, withContent, enabled, events, resolution } = this.args.dataModel;
      this.verify.verifyDataOutIntegration.perform({
        type: this.args.integration.id,
        settings,
        withContent,
        enabled,
        events,
        resolution,
      });
    } catch (err) {
      // do nothing, as it's only validation check before sending test
    }
  }

  @action toggleWithContent(checked: boolean) {
    this.args.dataModel.set('withContent', checked);
  }

  @action
  setSelectedEvents({ selectedItems }: ToggleDelta) {
    this.args.dataModel.set('events', selectedItems);
  }

  @action
  changeFieldValue(key: string, value: string) {
    this.args.dataModel.set('settings', {
      ...this.args.dataModel.settings,
      [key]: value,
    });
    this.validations.validateAttribute(key);
  }

  @action
  async onSave(e: Event) {
    e?.preventDefault();
    try {
      // this will throw if any field is invalid
      await this.validations.validateModel();

      return this.args.saveIntegrationTask.perform();
    } catch (err) {
      // do nothing, as it's only validation check before sending test
      return;
    }
  }

  @action
  onRemove() {
    this.confirmAndRemoveModal = true;
  }

  @action
  onRemoveModalClose() {
    this.confirmAndRemoveModal = false;
  }

  @action
  onConfirmAndDeleteClose() {
    this.onRemoveModalClose();
    return this.args.removeIntegrationTask.perform();
  }

  @action
  cancel() {
    this.router.transitionTo('integrations');
  }

  @action
  onReset() {
    this.args.dataModel.rollbackAttributes();
  }
}
